import { GlobeIcon, MessageSquareIcon, ShuffleIcon, ZapIcon } from "lucide-react";
import { useCallback } from "react";

import { UrlBar } from "./UrlBar";
import { usePromptSharePinnedVersionData } from "./usePromptSharePinnedVersionData";

import {
  PinnedVersionSelector,
  ShareSessionCardToggle,
  ShareSessionModeSelector,
  SharedSessionCard,
  buildVersionUpdateFields,
  deriveSharedSessionVersionSelection,
  sessionUrl,
  type PinnedVersionSelection,
} from "@/components/SharedSessions";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { EntityType } from "@/queries/folder-entities/types";
import type { SharedSession } from "@/queries/prompts";
import { cn } from "@/lib/utils";

export const SessionCard = ({
  index,
  session,
  onUpdate,
  onDelete,
  hideModeSelector = false,
}: {
  index: number;
  session: SharedSession;
  onUpdate: (
    fields: Partial<
      Pick<
        SharedSession,
        | "name"
        | "is_public"
        | "batch_enabled"
        | "chat_enabled"
        | "remix_enabled"
        | "label_name"
        | "version_number"
      >
    >,
  ) => void;
  onDelete: () => void;
  hideModeSelector?: boolean;
}) => {
  const { versions, labels } = usePromptSharePinnedVersionData();
  const versionSelection = deriveSharedSessionVersionSelection(session);

  const handleVersionChange = useCallback(
    (selection: PinnedVersionSelection) => {
      onUpdate(buildVersionUpdateFields(selection));
    },
    [onUpdate],
  );

  const leadingIcon = (
    <div
      className={cn(
        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full",
        session.resource_type === "prompt_template" ? "bg-amber-50" : "bg-violet-50",
      )}
    >
      <EntityIcon
        type={session.resource_type === "prompt_template" ? EntityType.PROMPT : EntityType.WORKFLOW}
        size="xs"
      />
    </div>
  );

  const extraHeaderBadges = (
    <>
      {!hideModeSelector && session.batch_enabled && (
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
          <ZapIcon className="h-2.5 w-2.5" /> Batch
        </span>
      )}
      {!hideModeSelector && session.chat_enabled && (
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
          <MessageSquareIcon className="h-2.5 w-2.5" /> Chat
        </span>
      )}
      {session.remix_enabled && (
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
          <ShuffleIcon className="h-2.5 w-2.5" /> Remix
        </span>
      )}
    </>
  );

  const expandedSettings = (
    <>
      <UrlBar url={sessionUrl(session.share_token)} />
      <div className="mt-2 border-t border-gray-100 pt-1 dark:border-gray-700">
        <ShareSessionCardToggle
          icon={GlobeIcon}
          label="Public access"
          checked={session.is_public}
          onChange={value => onUpdate({ is_public: value })}
        />
        {!hideModeSelector && (
          <ShareSessionModeSelector
            variant="mini"
            mode={session.batch_enabled ? "batch" : "chat"}
            onChange={mode =>
              onUpdate({
                batch_enabled: mode === "batch",
                chat_enabled: mode === "chat",
              })
            }
          />
        )}
        <PinnedVersionSelector
          variant="mini"
          value={versionSelection}
          onChange={handleVersionChange}
          versions={versions}
          labels={labels}
          selectTriggerClassName="h-6 w-[140px]"
        />
        <ShareSessionCardToggle
          icon={ShuffleIcon}
          label="Remixing"
          checked={session.remix_enabled}
          onChange={value => onUpdate({ remix_enabled: value })}
        />
      </div>
    </>
  );

  return (
    <SharedSessionCard
      animationIndex={index}
      session={session}
      onUpdate={onUpdate}
      onDelete={onDelete}
      leadingIcon={leadingIcon}
      extraHeaderBadges={extraHeaderBadges}
      expandedSettings={expandedSettings}
    />
  );
};
